import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { CodeBlock } from "@/components/ui/code-block";
import { cn } from "@/lib/utils";
import type { MasterControlPromptAtlasEntry } from "@/hooks/useMasterControl";

type MCPromptAtlasProps = {
  entries: MasterControlPromptAtlasEntry[];
  loading: boolean;
  onRefresh: () => Promise<void>;
  onEdit: (promptId: string, prompt: string) => Promise<void>;
  onReset: (promptId: string) => Promise<void>;
  canEdit: boolean;
};

export function MCPromptAtlas({ entries, loading, onRefresh, onEdit, onReset, canEdit }: MCPromptAtlasProps) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [selected, setSelected] = useState<MasterControlPromptAtlasEntry | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const overrides = useMemo(() => entries.filter((entry) => entry.hasOverride).length, [entries]);
  const categories = useMemo(() => {
    const map = new Map<
      string,
      {
        label: string;
        count: number;
        overrides: number;
      }
    >();
    entries.forEach((entry) => {
      const name = entry.category || "Other";
      const bucket = map.get(name) ?? { label: name, count: 0, overrides: 0 };
      bucket.count += 1;
      if (entry.hasOverride) {
        bucket.overrides += 1;
      }
      map.set(name, bucket);
    });
    return Array.from(map.entries())
      .map(([id, meta]) => ({ id, ...meta }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [entries]);
  const [activeCategory, setActiveCategory] = useState<string>(() => categories[0]?.id ?? "");
  useEffect(() => {
    if (!categories.length) {
      setActiveCategory("");
      return;
    }
    const exists = categories.some((category) => category.id === activeCategory);
    if (!exists) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);
  const activeMeta = categories.find((category) => category.id === activeCategory) ?? null;
  const visibleEntries = useMemo(() => {
    if (!entries.length) return [];
    if (!activeCategory) return entries;
    return entries.filter((entry) => entry.category === activeCategory);
  }, [activeCategory, entries]);

  const handleOpen = (entry: MasterControlPromptAtlasEntry) => {
    setSelected(entry);
    setEditorOpen(true);
  };

  const handleClose = () => {
    setEditorOpen(false);
    setSelected(null);
  };

  const triggerRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Card className="rounded-2xl border border-border bg-background/60 text-foreground">
      <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-xl font-semibold text-foreground">Prompt atlas</CardTitle>
          <p className="text-sm text-muted-foreground">
            Directory of MCP prompts grouped by category with file paths, overrides, and editing controls.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="rounded-full border border-border/10 px-3 py-1 font-mono text-primary-foreground dark:text-amber-200">
            Overrides: {overrides}/{entries.length}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400/60 text-primary-foreground dark:text-amber-100 hover:bg-amber-500/10"
            disabled={loading || refreshing}
            onClick={() => {
              void triggerRefresh();
            }}
          >
            {loading || refreshing ? "Refreshing…" : "Refresh atlas"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading prompts…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No prompts discovered.</p>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Categories</p>
              <div className="mt-3 space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    className={cn(
                      "w-full rounded-xl border px-3 py-2 text-left transition hover:border-amber-400 hover:bg-amber-500/5",
                      activeCategory === category.id
                        ? "border-amber-400 bg-amber-500/5 text-foreground"
                        : "border-border/10 text-foreground/70",
                    )}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{category.label}</span>
                      <span className="text-xs text-muted-foreground">
                        {category.overrides}/{category.count}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Selected category</p>
                  <h3 className="text-lg font-semibold text-foreground">{activeMeta?.label ?? categories[0]?.label}</h3>
                  {activeMeta && (
                    <p className="text-xs text-muted-foreground">
                      {activeMeta.overrides}/{activeMeta.count} overrides active
                    </p>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {visibleEntries.length} prompt{visibleEntries.length === 1 ? "" : "s"}
                </div>
              </div>
              {visibleEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground">No prompts available for this category.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/5 text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2">Prompt</th>
                        <th className="px-3 py-2">Source</th>
                        <th className="px-3 py-2 w-32 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-foreground/80">
                      {visibleEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td className="px-3 py-3 align-top">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 text-foreground">
                                <span className="font-semibold">{entry.label}</span>
                                {entry.hasOverride && (
                                  <Badge className="bg-amber-500/20 text-primary-foreground dark:text-amber-200">Override</Badge>
                                )}
                              </div>
                              {entry.description && <p className="text-xs text-muted-foreground">{entry.description}</p>}
                            </div>
                          </td>
                          <td className="px-3 py-3 align-top font-mono text-xs text-foreground/70">
                            <div className="rounded border border-border/5 bg-background/5 px-2 py-1 text-foreground/80">
                              {entry.filePath || "—"}
                            </div>
                            <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                              {entry.module}.{entry.attribute}
                            </p>
                          </td>
                          <td className="px-3 py-3 text-right align-top">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-primary-foreground dark:text-amber-200 hover:text-primary-foreground dark:text-amber-100"
                              onClick={() => handleOpen(entry)}
                            >
                              {canEdit ? "Edit prompt" : "View prompt"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
      <PromptEditorDialog
        entry={selected}
        open={editorOpen}
        onOpenChange={(next) => {
          if (!next) handleClose();
        }}
        onSave={async (value) => {
          if (!selected) return;
          await onEdit(selected.id, value);
          handleClose();
        }}
        onReset={async () => {
          if (!selected) return;
          await onReset(selected.id);
          handleClose();
        }}
        canEdit={canEdit}
      />
    </Card>
  );
}

type PromptEditorDialogProps = {
  entry: MasterControlPromptAtlasEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (prompt: string) => Promise<void>;
  onReset: () => Promise<void>;
  canEdit: boolean;
};

function PromptEditorDialog({ entry, open, onOpenChange, onSave, onReset, canEdit }: PromptEditorDialogProps) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    setDraft(entry?.prompt ?? "");
  }, [entry]);

  const handleSave = async () => {
    if (!entry) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!entry) return;
    setResetting(true);
    try {
      await onReset();
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-background p-6 shadow-2xl">
          {entry ? (
            <div className="space-y-5 text-sm text-foreground">
              <div className="flex flex-col gap-2">
                <Dialog.Title className="text-xl font-semibold text-foreground">{entry.label} prompt</Dialog.Title>
                <Dialog.Description className="text-sm text-muted-foreground">
                  {entry.description ?? "Edit the system prompt used by this MCP agent."}
                </Dialog.Description>
                {entry.category && (
                  <p className="text-[11px] uppercase tracking-wide text-amber-300">{entry.category}</p>
                )}
                <div className="text-xs text-muted-foreground">
                  <span className="font-mono">{entry.filePath || "—"}</span>
                  <span className="mx-2 text-muted-foreground/70">•</span>
                  <span className="font-mono">
                    {entry.module}.{entry.attribute}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
                  <span>{entry.hasOverride ? "Override prompt" : "Current prompt"}</span>
                  {!canEdit && <span className="text-primary-foreground dark:text-amber-200">Read only</span>}
                </div>
                <Textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  rows={10}
                  className="font-mono text-xs text-amber-50"
                  disabled={!canEdit}
                />
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Default prompt</p>
                <CodeBlock value={entry.defaultPrompt} language="txt" collapsible />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <div className="text-xs text-muted-foreground">Overrides persist instantly across the platform.</div>
                <div className="flex flex-wrap gap-2">
                  {entry.hasOverride && canEdit && (
                    <Button variant="outline" size="sm" disabled={resetting} onClick={() => void handleReset()}>
                      {resetting ? "Resetting…" : "Revert to default"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    disabled={!canEdit || saving || !draft.trim()}
                    onClick={() => void handleSave()}
                  >
                    {saving ? "Saving…" : "Save prompt"}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Select a prompt to view details.</p>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default MCPromptAtlas;
